import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  DownloadOutlined,
  UploadFileOutlined,
  RefreshOutlined,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  {
    header: "Item Code",
    field: "itemCode",
  },
  {
    header: "Today Production",
    field: "todayProduction",
    number: true,
  },
  {
    header: "Total Till Date Production",
    field: "totalTillDateProduction",
    number: true,
  },
];

const EXPECTED_HEADERS = COLUMNS.map((column) => column.header);

const normalize = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const isBlank = (value) =>
  value === null || value === undefined || value === "";

// Returns null for a blank cell and NaN for text that is not a number (reported as a row error).
const toNumber = (value) => {
  if (isBlank(value)) {
    return null;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : NaN;
  }

  const text = String(value).replace(/,/g, "").trim();

  if (!text) {
    return null;
  }

  return /^[-+]?(\d+\.?\d*|\.\d+)$/.test(text) ? Number(text) : NaN;
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const authHeader = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");

  return {
    headers: {
      Authorization: `Bearer ${user?.accessToken}`,
    },
  };
};

const getError = (error, fallback) =>
  error?.response?.data?.Exception ||
  error?.response?.data?.message ||
  fallback;

const ProductionReport = () => {
  const fileInputRef = useRef(null);

  const [file, setFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [currentRows, setCurrentRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadCurrentReport = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `${API}/production-report/current`,
        authHeader(),
      );

      setCurrentRows(response.data || []);
    } catch (err) {
      setCurrentRows([]);
      setError(getError(err, "Unable to load the current production report."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrentReport();
  }, []);

  const handleDownloadTemplate = () => {
    setError("");
    setSuccess("");

    const worksheet = XLSX.utils.json_to_sheet(
      [Object.fromEntries(COLUMNS.map((c) => [c.header, ""]))],
      {
        header: EXPECTED_HEADERS,
      },
    );

    XLSX.utils.sheet_add_json(worksheet, [], {
      origin: "A2",
      skipHeader: true,
    });

    worksheet["!cols"] = [{ wch: 22 }, { wch: 22 }, { wch: 28 }];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Production Report");

    XLSX.writeFile(workbook, "ProcureTrack_Production_Report_Template.xlsx");

    setSuccess("Production report template downloaded.");
  };

  const validateHeaders = (headers) => {
    const actual = headers.map(normalize);

    const missing = EXPECTED_HEADERS.filter(
      (header) => !actual.includes(header),
    );

    if (missing.length) {
      throw new Error(
        `The Excel file is missing these columns: ${missing.join(", ")}`,
      );
    }

    const extra = actual.filter((header) => !EXPECTED_HEADERS.includes(header));

    if (extra.length) {
      throw new Error(
        `The Excel file contains unsupported columns: ${extra.join(
          ", ",
        )}. Please use the downloaded template.`,
      );
    }
  };

  const convertRows = (rawRows) => {
    const grouped = new Map();

    rawRows.forEach((rawRow, index) => {
      const itemCode = normalize(rawRow["Item Code"]);

      if (!itemCode) {
        throw new Error(`Row ${index + 2}: Item Code is required.`);
      }

      const todayProduction = toNumber(rawRow["Today Production"]);

      const totalTillDateProduction = toNumber(
        rawRow["Total Till Date Production"],
      );

      if (Number.isNaN(todayProduction)) {
        throw new Error(`Row ${index + 2}: Today Production must be a number.`);
      }

      if (Number.isNaN(totalTillDateProduction)) {
        throw new Error(
          `Row ${index + 2}: Total Till Date Production must be a number.`,
        );
      }

      if (todayProduction !== null && todayProduction < 0) {
        throw new Error(
          `Row ${index + 2}: Today Production cannot be negative.`,
        );
      }

      if (totalTillDateProduction !== null && totalTillDateProduction < 0) {
        throw new Error(
          `Row ${index + 2}: Total Till Date Production cannot be negative.`,
        );
      }

      const existing = grouped.get(itemCode);

      if (!existing) {
        grouped.set(itemCode, {
          id: itemCode,
          itemCode,
          todayProduction: todayProduction ?? 0,
          totalTillDateProduction: totalTillDateProduction,
        });

        return;
      }

      existing.todayProduction += todayProduction ?? 0;

      if (totalTillDateProduction !== null) {
        existing.totalTillDateProduction = totalTillDateProduction;
      }
    });

    return Array.from(grouped.values());
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    setFile(null);
    setPreviewRows([]);
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const lower = selectedFile.name.toLowerCase();

      if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
        throw new Error("Please upload an Excel file (.xlsx or .xls).");
      }

      const buffer = await selectedFile.arrayBuffer();

      const workbook = XLSX.read(buffer, {
        type: "array",
        raw: true,
      });

      if (!workbook.SheetNames.length) {
        throw new Error("The Excel file does not contain a sheet.");
      }

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      const matrix = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: "",
        raw: true,
      });

      if (!matrix.length) {
        throw new Error("The Excel file is empty.");
      }

      validateHeaders(matrix[0]);

      const rawRows = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
        raw: true,
      });

      if (!rawRows.length) {
        throw new Error("The Excel file has no production rows.");
      }

      const converted = convertRows(rawRows);

      setFile(selectedFile);
      setPreviewRows(converted);
      setSuccess(`${converted.length} item codes ready to upload.`);
    } catch (err) {
      setError(err.message || "Unable to read the production report.");
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  const handleUpload = async () => {
    if (!previewRows.length) {
      setError("Please choose a production report first.");
      return;
    }

    try {
      setUploading(true);
      setError("");
      setSuccess("");

      const payload = previewRows.map((row) => ({
        itemCode: row.itemCode,
        todayProduction: row.todayProduction ?? 0,
        totalTillDateProduction: row.totalTillDateProduction,
      }));

      const response = await axios.post(
        `${API}/production-report/upload`,
        payload,
        authHeader(),
      );

      setSuccess(response.data || "Production report uploaded successfully.");

      setFile(null);
      setPreviewRows([]);

      await loadCurrentReport();
    } catch (err) {
      setError(getError(err, "Unable to upload the production report."));
    } finally {
      setUploading(false);
    }
  };

  const currentColumns = [
    {
      field: "itemCode",
      headerName: "Item Code",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "todayProduction",
      headerName: "Today Production",
      flex: 1,
      minWidth: 180,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
    {
      field: "totalTillDateProduction",
      headerName: "Total Till Date Production",
      flex: 1,
      minWidth: 230,
      type: "number",
      valueFormatter: (value) => formatNumber(value),
    },
  ];

  const previewColumns = [...currentColumns];

  const linkedReportName = currentRows[0]?.uploadFileName || null;

  return (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2.5, md: 3 },
        backgroundColor: "#f5f7fa",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ mb: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            sx={{
              fontSize: { xs: 24, md: 28 },
              fontWeight: 700,
              color: "#172033",
            }}
          >
            Production Report
          </Typography>

          <Chip
            label="Daily"
            size="small"
            sx={{
              backgroundColor: "#eef4ff",
              color: "#175cd3",
              fontWeight: 600,
            }}
          />
        </Stack>

        <Typography
          sx={{
            mt: 0.5,
            fontSize: 13,
            color: "#667085",
          }}
        >
          Upload today's item production against the latest buyer report.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e4e7ec",
          borderRadius: 2,
          backgroundColor: "#fff",
          mb: 2,
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
          }}
        >
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: "#172033",
            }}
          >
            Upload Production Report
          </Typography>

          <Typography
            sx={{
              mt: 0.5,
              fontSize: 12,
              color: "#667085",
            }}
          >
            The report is automatically linked to the latest buyer combined
            report.
          </Typography>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            gap={1}
            sx={{ mt: 2 }}
          >
            <Button
              className="btn-outlined"
              startIcon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>

            <Button
              className="btn-contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <UploadFileOutlined />
                )
              }
              disabled={loading}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose Excel File
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleFileChange}
            />
          </Stack>

          {file && (
            <Typography
              sx={{
                mt: 1.5,
                fontSize: 12,
                color: "#475467",
              }}
            >
              Selected file: <strong>{file.name}</strong>
            </Typography>
          )}
        </Box>
      </Paper>

      {previewRows.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e4e7ec",
            borderRadius: 2,
            backgroundColor: "#fff",
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #eaecf0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#172033",
                }}
              >
                Upload Preview
              </Typography>

              <Typography
                sx={{
                  mt: 0.25,
                  fontSize: 12,
                  color: "#667085",
                }}
              >
                Duplicate item codes are combined before saving.
              </Typography>
            </Box>

            <Button
              className="btn-contained"
              disabled={uploading}
              onClick={handleUpload}
              startIcon={
                uploading ? (
                  <CircularProgress size={15} />
                ) : (
                  <UploadFileOutlined />
                )
              }
            >
              Upload Production Report
            </Button>
          </Box>

          <Box sx={{ p: 1.5 }}>
            <DataGrid
              autoHeight
              rows={previewRows}
              columns={previewColumns}
              disableRowSelectionOnClick
              hideFooter
              density="compact"
            />
          </Box>
        </Paper>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e4e7ec",
          borderRadius: 2,
          backgroundColor: "#fff",
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #eaecf0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: "#172033",
              }}
            >
              Current Production Data
            </Typography>

            <Typography
              sx={{
                mt: 0.25,
                fontSize: 12,
                color: "#667085",
              }}
            >
              {linkedReportName
                ? `Linked to ${linkedReportName}`
                : "No production data for the latest buyer report."}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            startIcon={<RefreshOutlined />}
            onClick={loadCurrentReport}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        <Box sx={{ p: 1.5 }}>
          <DataGrid
            autoHeight
            rows={currentRows.map((row) => ({
              ...row,
              id: row.productionReportId,
            }))}
            columns={currentColumns}
            loading={loading}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 25,
                  page: 0,
                },
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ProductionReport;
